import React, { useMemo } from 'react';
import { Grid, Switch } from '@arco-design/web-react';
import { useBlock, useFocusIdx } from '@bay-email/editor';
import { t } from '@bay-email/core';
import { Form, useFormState } from 'react-final-form';
import { get } from 'lodash';

import { InputWithUnitField } from '../../../../components/Form';
import { pixelAdapter } from '../../adapter';

const normalize = (value: unknown) => String(value ?? '').trim();

function parseRadius(radius: string | undefined) {
	const parts = normalize(radius).split(/\s+/).filter(Boolean);

	if (parts.length === 0) {
		return { topLeft: '0px', topRight: '0px', bottomRight: '0px', bottomLeft: '0px' };
	}

	if (parts.length === 1) {
		const v = parts[0];
		return { topLeft: v, topRight: v, bottomRight: v, bottomLeft: v };
	}

	if (parts.length === 2) {
		return {
			topLeft: parts[0],
			topRight: parts[1],
			bottomRight: parts[0],
			bottomLeft: parts[1],
		};
	}

	if (parts.length === 3) {
		return {
			topLeft: parts[0],
			topRight: parts[1],
			bottomRight: parts[2],
			bottomLeft: parts[1],
		};
	}

	return {
		topLeft: parts[0],
		topRight: parts[1],
		bottomRight: parts[2],
		bottomLeft: parts[3],
	};
}

function RadiusChangeSync({ onChange }: { onChange: (value: string) => void }) {
	const {
		values: { topLeft, topRight, bottomRight, bottomLeft },
	} = useFormState<{
		topLeft: string;
		topRight: string;
		bottomRight: string;
		bottomLeft: string;
	}>();

	React.useEffect(() => {
		onChange(
			[
				normalize(topLeft) || '0px',
				normalize(topRight) || '0px',
				normalize(bottomRight) || '0px',
				normalize(bottomLeft) || '0px',
			].join(' '),
		);
	}, [topLeft, topRight, bottomRight, bottomLeft, onChange]);

	return null;
}

export function BorderRadiusControl() {
	const { focusIdx } = useFocusIdx();
	const { values, change } = useBlock();

	const borderRadiusPath = `${focusIdx}.attributes.border-radius`;
	const borderRadius = get(values, borderRadiusPath) as string | undefined;
	const parsedRadius = useMemo(() => parseRadius(borderRadius), [borderRadius]);

	const radiusParts = normalize(borderRadius).split(/\s+/).filter(Boolean);
	const isLinked = radiusParts.length <= 1;

	const handleLinkChange = (checked: boolean) => {
		if (checked) {
			const first =
				normalize(borderRadius).split(/\s+/).filter(Boolean)[0] ||
				normalize(parsedRadius.topLeft) ||
				'0px';
			change(borderRadiusPath, first);
			return;
		}

		const base =
			normalize(borderRadius).split(/\s+/).filter(Boolean)[0] ||
			normalize(parsedRadius.topLeft) ||
			'0px';
		change(borderRadiusPath, `${base} ${base} ${base} ${base}`);
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					marginBottom: '12px',
					gap: '12px',
				}}
			>
				<span>{t('Link Radius')}</span>
				<Switch
					checked={isLinked}
					onChange={handleLinkChange}
					size='small'
				/>
			</div>

			{isLinked ? (
				<Grid.Row gutter={16}>
					<Grid.Col span={12}>
						<InputWithUnitField
							label={t('Radius (px)')}
							name={borderRadiusPath}
							config={pixelAdapter}
						/>
					</Grid.Col>
				</Grid.Row>
			) : (
				<Form<{
					topLeft: string;
					topRight: string;
					bottomRight: string;
					bottomLeft: string;
				}>
					initialValues={parsedRadius}
					enableReinitialize
					onSubmit={() => {}}
					subscription={{ values: true }}
				>
					{() => (
						<>
							<Grid.Row gutter={16}>
								<Grid.Col span={12}>
									<InputWithUnitField
										label={t('Top Left')}
										name='topLeft'
										config={pixelAdapter}
									/>
								</Grid.Col>
								<Grid.Col span={12}>
									<InputWithUnitField
										label={t('Top Right')}
										name='topRight'
										config={pixelAdapter}
									/>
								</Grid.Col>
							</Grid.Row>
							<Grid.Row gutter={16}>
								<Grid.Col span={12}>
									<InputWithUnitField
										label={t('Bottom Right')}
										name='bottomRight'
										config={pixelAdapter}
									/>
								</Grid.Col>
								<Grid.Col span={12}>
									<InputWithUnitField
										label={t('Bottom Left')}
										name='bottomLeft'
										config={pixelAdapter}
									/>
								</Grid.Col>
							</Grid.Row>
							<RadiusChangeSync onChange={value => change(borderRadiusPath, value)} />
						</>
					)}
				</Form>
			)}
		</>
	);
}
